import { bindEvent, createRealtimeClient, type Pusher } from '@nexo/api';
import { realtimeChannels, type Team } from '@nexo/types';
import { useToast } from '@nexo/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { useMe } from '../features/auth/AuthProvider';
import { notify, playSound } from '../features/notifications/alerts';
import { notificationText } from '../features/notifications/notificationText';
import { api, SESSION_EXPIRED_EVENT } from '../lib/api';
import { cache } from '../lib/cache';
import { t } from '../lib/i18n';
import { keys } from '../lib/queries';

export type RealtimeStatus = 'disabled' | 'connecting' | 'connected' | 'unavailable';

interface RealtimeContextValue {
  client: Pusher | null;
  status: RealtimeStatus;
  /** Channel currently on screen: its incoming messages are marked read instead of unread. */
  activeChannelRef: RefObject<string | null>;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/**
 * Owns the realtime connection for the signed-in user and handles user-level events
 * (notifications, private channels/DMs created for them). Team-level subscriptions live in
 * `useTeamRealtime`.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const activeChannelRef = useRef<string | null>(null);
  const [client, setClient] = useState<Pusher | null>(null);
  const [connectionStatus, setStatus] = useState<RealtimeStatus>('connecting');

  const { data: config, isError } = useQuery({
    queryKey: keys.realtimeConfig,
    queryFn: api.realtime.config,
    staleTime: Infinity,
  });

  const status: RealtimeStatus = isError
    ? 'unavailable'
    : config?.enabled === false
      ? 'disabled'
      : connectionStatus;

  useEffect(() => {
    if (!config?.enabled) return;
    const pusher = createRealtimeClient({ config, authorize: api.realtime.authorize });
    let wasConnected = false;
    pusher.connection.bind('state_change', ({ current }: { current: string }) => {
      if (current === 'connected') {
        setStatus('connected');
        // Catch up on anything missed while disconnected.
        if (wasConnected) void queryClient.invalidateQueries();
        wasConnected = true;
      } else if (current === 'connecting' || current === 'initialized') {
        setStatus('connecting');
      } else {
        setStatus('unavailable');
      }
    });
    pusher.connect();
    // Storing an external connection created by this effect is the intended use of state here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setClient(pusher);
    return () => {
      pusher.disconnect();
      setClient(null);
    };
  }, [config, queryClient]);

  // User-level events.
  useEffect(() => {
    if (!client) return;
    const channel = client.subscribe(realtimeChannels.user(me.id));
    const unbind = [
      bindEvent(channel, 'notification:created', (notification) => {
        // A mention in the conversation on screen is already seen: store it as read, no alert.
        const seen =
          notification.data.type === 'mention' &&
          activeChannelRef.current === notification.data.channelId &&
          document.visibilityState === 'visible';
        const stored = seen ? { ...notification, readAt: new Date().toISOString() } : notification;
        queryClient.setQueryData(keys.notifications, (list: (typeof notification)[] | undefined) =>
          list ? [stored, ...list] : list,
        );
        if (seen) {
          void api.notifications.markRead(notification.id);
          return;
        }
        if (notification.data.type === 'team_invite')
          void queryClient.invalidateQueries({ queryKey: keys.myInvites });
        const { title, href } = notificationText(notification);
        // Mentions already alert through the message itself (see alertForMessage).
        if (notification.data.type !== 'mention') {
          playSound(notification.data.type === 'meeting_starting' ? 'call' : 'mention');
          void notify({
            title,
            body: notification.actor?.displayName,
            tag: notification.id,
            onClick: () => void navigate(href),
          });
        }
        toast({
          title,
          description: notification.actor?.displayName,
          onClick: () => void navigate(href),
        });
      }),
      bindEvent(channel, 'channel:created', (created) => cache.upsertChannel(queryClient, created)),
      bindEvent(channel, 'member:updated', () => {
        void queryClient.invalidateQueries({ queryKey: keys.teams });
      }),
      // Removed from a team: drop it; TeamShell redirects home if it was open.
      bindEvent(channel, 'member:removed', ({ teamId }) => {
        queryClient.setQueryData<Team[]>(keys.teams, (list) =>
          list?.filter((t) => t.id !== teamId),
        );
        toast({ title: t('Ya no formas parte de un equipo') });
      }),
      // Closed from another device: if it is this one, sign out now.
      bindEvent(channel, 'session:revoked', ({ sessionIds }) => {
        void queryClient.invalidateQueries({ queryKey: keys.sessions });
        const current = api.auth.currentSessionId();
        if (current && sessionIds.includes(current)) {
          api.http.tokens.clear();
          window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        }
      }),
    ];
    return () => {
      unbind.forEach((fn) => fn());
      client.unsubscribe(realtimeChannels.user(me.id));
    };
  }, [client, me.id, queryClient, toast, navigate]);

  // Without a live connection, poll so the app still works (slower) in every environment.
  useEffect(() => {
    if (status === 'connected' || status === 'connecting') return;
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['team'] });
      void queryClient.invalidateQueries({ queryKey: ['channel'] });
      void queryClient.invalidateQueries({ queryKey: keys.notifications });
    }, 10_000);
    return () => clearInterval(timer);
  }, [status, queryClient]);

  const value = useMemo(() => ({ client, status, activeChannelRef }), [client, status]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useRealtime must be used inside <RealtimeProvider>');
  return context;
}
